import { FeatureFlag, StorageKey } from '@revelio/core';
import {
  COMPOSITION_GET_DATA_V3,
  type CompositionV3DataQuery,
  type CompositionV3DataQueryVariables,
  METRIC_MODE_IDS,
  POSTING_GET_DATA,
  POSTING_SOURCE_IDS,
  POSTINGS_V3_GET_DATA,
  type PostingsDataQuery,
  type PostingsV3DataQuery,
} from '@revelio/data-access';

import { useUnleashFlag } from '../../../../../shared/unleash-flags/use-unleash-flag';
import { useMonthApiFilters } from '../../../../entity-summary/use-month-api-filters';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { useAvailableDateRangeWithTrialLag } from '../../../filters/hooks/useAvailableDateRangeWithTrialLag';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { hasPrimaryFilter } from '../../../filters/utils/common';
import { getCompositionV3Variables } from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { getCompositionV3MonthRefs } from '../../../filters/utils/composition-v3/month-refs-from-v3-filters';
import {
  getFormattedDateString,
  utcToLocal,
} from '../../../filters/utils/dateUtils';
import { getDim1 } from '../../../filters/utils/getDim1';
import { getPostingFilters } from '../../../filters/utils/getPostingFilters';
import { getPostingsV3Variables } from '../../../filters/utils/getPostingsV3Variables';
import { DataProviderQueryStatus } from '../types';
import { useUrqlQueryWithStatus } from '../utils/data-provider-results-utils';
import { usePostingsV3WeekRefs } from '../utils/usePostingsV3WeekRefs';
import { convertPostingsV3ToMarketPostings } from './convert-postings-v3-to-market-metrics';
import { combineTwoQueryStatuses } from './market-condition-metrics-utils';

type PostingResponse = PostingsDataQuery['posting'];

export function useCompositionAndPostingsQueries(
  primaryDimension: PrimaryFilterId
) {
  const isPostingsV3Enabled = useUnleashFlag(FeatureFlag.PostingsV3);
  const { activeFilterMap } = useFilterContext();

  const compositionEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'month',
  });
  const dateRangeApiFilters = useMonthApiFilters();

  const compositionVariablesV3Base = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters: compositionEffectiveFilters,
    isSnapshot: false,
  });

  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const availableStartDate = utcToLocal(availableDateRange?.month.min);
  const availableEndDate = utcToLocal(availableDateRange?.month.max);
  const endDate =
    compositionVariablesV3Base.filters.end_date ??
    getFormattedDateString(availableEndDate, 'month');
  const startDate =
    compositionVariablesV3Base.filters.start_date ??
    getFormattedDateString(availableStartDate, 'month');

  const compositionVariablesV3 = {
    ...compositionVariablesV3Base,
    filters: {
      ...compositionVariablesV3Base.filters,
      start_date: startDate,
      end_date: endDate,
    },
  };

  const compositionPausedV3 =
    !compositionVariablesV3.entities ||
    (Array.isArray(compositionVariablesV3.entities) &&
      compositionVariablesV3.entities.length === 0) ||
    !startDate ||
    !endDate;

  const [{ data: compositionDataUnion, status: compositionStatus }] =
    useUrqlQueryWithStatus<
      CompositionV3DataQuery,
      CompositionV3DataQueryVariables
    >({
      query: COMPOSITION_GET_DATA_V3,
      variables: compositionVariablesV3,
      pause: compositionPausedV3,
    });

  const dim1 = getDim1(primaryDimension);
  const postingEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'week',
  });
  const postingFilters = getPostingFilters(
    primaryDimension,
    postingEffectiveFilters
  );
  const isPrimaryFilterSet = hasPrimaryFilter({
    primaryDimension,
    filters: compositionEffectiveFilters,
  });

  const postingsPaused = !isPrimaryFilterSet || !dateRangeApiFilters;

  const [{ data: postingsDataV2, status: postingsStatusV2 }] =
    useUrqlQueryWithStatus<PostingsDataQuery>({
      query: POSTING_GET_DATA,
      variables: {
        dim1,
        filters: {
          ...postingFilters,
          provider: [POSTING_SOURCE_IDS.unified],
          metric_mode: METRIC_MODE_IDS.expectedHires,
        },
        ...(localStorage.getItem(StorageKey.SkipShareOfPostingsScaling) ===
        'true'
          ? { skip_share_of_postings_scaling: true }
          : {}),
      },
      pause: isPostingsV3Enabled || postingsPaused,
    });

  const postingsV3Variables = getPostingsV3Variables({
    primaryDimension,
    effectiveFilters: postingEffectiveFilters,
    defaultMetricMode: 'expected_hires',
    activeFilterMap,
  });

  const {
    weekRefs,
    isLoading: isWeekRefsLoading,
    error: weekRefsError,
  } = usePostingsV3WeekRefs({ pause: !isPostingsV3Enabled });

  const [{ data: postingsDataV3, status: postingsStatusV3 }] =
    useUrqlQueryWithStatus<PostingsV3DataQuery>({
      query: POSTINGS_V3_GET_DATA,
      variables: postingsV3Variables,
      pause: !isPostingsV3Enabled || postingsPaused || isWeekRefsLoading,
    });

  const monthRefs = getCompositionV3MonthRefs({
    availableDateRange,
    filters: compositionVariablesV3.filters,
  });

  if (isPostingsV3Enabled) {
    const postingsData: PostingResponse = convertPostingsV3ToMarketPostings({
      data: postingsDataV3,
      weekRefs,
    });

    const status = weekRefsError
      ? DataProviderQueryStatus.Error
      : combineTwoQueryStatuses(compositionStatus, postingsStatusV3);

    return {
      compositionData: compositionDataUnion?.composition_v3,
      monthRefs,
      postingsData,
      status,
    };
  }

  const status = combineTwoQueryStatuses(compositionStatus, postingsStatusV2);

  return {
    compositionData: compositionDataUnion?.composition_v3,
    monthRefs,
    postingsData: postingsDataV2?.posting,
    status,
  };
}
